import { MonetizationType } from '@/@types/graphql-types';
import type { StripeCheckoutMetadataEntry } from '@/@types/graphql-types';
import type { JWTVFreeTrialPlacement } from '@/components/experiments/JWTVFreeTrialExp/helper';
import { DEVICE_METADATA_VALUES } from '@/components/experiments/JwProPricingExp/helper';
import { SUBSCRIPTIONS } from '@/features/jw-pro/constants';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { Device } from '@/helpers/composables/useUserAgent';
import { priceFormatter } from '@/helpers/pro-web-helper';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { ProModal } from '@/lazy-load-components';
import type { ClickoutUrl } from '@/helpers/clickout-helper';
import { useExperiment } from '@/libraries/experiment';
import {
	getExperimentAffiliateTag,
	type ExperimentAffiliationPlacement,
} from '@/libraries/experiment/experiment-affiliation';
import { ExperimentVariant } from '@/libraries/experiment/types';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { computed } from 'vue';

const placement: Record<ExperimentVariant<'EXP_JW_PLUS_FREE_TRIAL'>, JWTVFreeTrialPlacement | null> = {
	control: null,
	variant_1: 'ft',
	variant_2: 'sqtv',
	variant_3: 'sqtv',
};

// Values the BE expects for the `placement` metadata key
const PLACEMENT_METADATA_VALUES: Record<JWTVFreeTrialPlacement, string> = {
	ft: 'free_trial',
	sqtv: 'sqtv',
	DEFAULT: 'pro_sign_up',
};

// isFreeTrial and placement are both required for the BE to create a free trial checkout session. Control gets the
// experiment keys only, as it isn't a free trial.
export function buildFreeTrialCheckoutMetadata(
	{ variant, placement }: { variant: string; placement?: JWTVFreeTrialPlacement },
	deviceType: Device
): StripeCheckoutMetadataEntry[] {
	return [
		{ key: 'device', value: DEVICE_METADATA_VALUES[deviceType] },
		{ key: 'experimentName', value: 'EXP_JW_PLUS_FREE_TRIAL' },
		{ key: 'experimentVariant', value: variant },
		...(variant === 'control' ? [] : [{ key: 'isFreeTrial', value: 'true' }]),
		...(placement ? [{ key: 'placement', value: PLACEMENT_METADATA_VALUES[placement] }] : []),
	];
}

export function useJWTVFreeTrialExp() {
	// 'global' as this is also read from modals opened via ModalHelper, which mount outside the activating component tree
	const { activeVariantWithControlGroup: variant, experimentInfo } = useExperiment(
		'EXP_JW_PLUS_FREE_TRIAL',
		'global'
	);

	const languageStore = useLanguageStore();

	const jwtvFreeTrialPricing = computed(() => {
		const proPrices = SUBSCRIPTIONS.regular.prices;
		const priceInfo = proPrices[languageStore.country] ?? proPrices.DEFAULT;

		return priceFormatter(languageStore.locale, priceInfo.price, priceInfo.currency);
	});

	const jwtvFreeTrialInfo = computed(() => {
		return { trialDays: 7, retailPrice: jwtvFreeTrialPricing.value, monetizationType: MonetizationType.Flatrate };
	});

	const jwtvFreeTrialExpPlacement = computed(() => {
		if (!variant.value) return null;

		return placement[variant.value];
	});

	function setJWTVFreeTrialExpParams(
		url: ClickoutUrl,
		placement: ExperimentAffiliationPlacement,
		providerShortName?: string
	) {
		if (variant.value !== 'control') return url;

		const tag = getExperimentAffiliateTag({
			affiliationShortId: experimentInfo.value.affiliationShortId as string,
			variant: variant.value,
			country: languageStore.country,
			placement,
			providerShortName,
		});

		return url.set('uct_ctag', tag);
	}

	// placement is decided by the calling component, as any variant can show a free trial in either spot
	function jwtvFreeTrialExpOpenProModal({
		placement,
		titlePath,
		location = 'FreeTrial',
		additionalContexts,
	}: {
		placement: JWTVFreeTrialPlacement;
		// sqtv: where the user lands (and the JWTV player opens) after checkout
		titlePath?: string | null;
		location?: string;
		additionalContexts?: SnowplowContext[];
	}) {
		return ModalHelper.openModal(
			ProModal,
			{ jwtvFreeTrialExpContext: { placement, titlePath: titlePath ?? null }, additionalContexts },
			{
				cssClass: 'pro-jw-pro-modal',
			},
			location,
			true,
			additionalContexts
		);
	}

	return {
		jwtvFreeTrialExpPlacement,
		jwtvFreeTrialInfo,
		jwtvFreeTrialExpOpenProModal,
		setJWTVFreeTrialExpParams,
	};
}
